import axios, { AxiosError, AxiosResponse } from 'axios';
import { environment } from '../../../../environments/environment';
import { EmployeeInvite } from '@med_poc/types';
import { cloneDeepWith } from 'lodash-es';

const trimExcludedKeyPattern = /(pass(word)?|token|secret|api[-_]?key|authorization|auth)/i;

const shouldSkipStringTrim = (key: unknown): boolean =>
  typeof key === 'string' && trimExcludedKeyPattern.test(key);

const deepTrimStrings = <T>(value: T): T =>
  cloneDeepWith(value, (currentValue, key) => {
    if (typeof currentValue !== 'string') return undefined;
    if (shouldSkipStringTrim(key)) return currentValue;
    return currentValue.trim();
  }) as T;

/**
 * Creates a new professional user group.
 *
 * @param description - Human-readable description or name for the professional group to create.
 * @returns A Promise that resolves to the Response, containing details of the created group or an error.
 *
 *
 * @example
 * // Initiates the request and logs the result or error to the console
 * await createProfessionalGroup("Cardiologists");
 */
export const createProfessionalGroup = async (description: string) => {

    let data = JSON.stringify({
        "description": description
    });

    let config = {
    method: 'post',
    maxBodyLength: Infinity,
    url: `${environment.apiUrl}api/usergroups/createProfessionalGroup`,
    headers: { 
        'Content-Type': 'application/json', 
    },
    data : data
    };

    return axios.request(config)
    .then((response) => {
        return response
    // console.log(JSON.stringify(response.data));
    })
    .catch((err: Error | AxiosError) => {


    /*     axiosErrorHandler<MyType>(res => {
      if (res.type === 'axios-error') {
         //type is available here
         const error = res.error;
      } else {
         //stock error
      }
   }
    */

        /* if (axios.isAxiosError(err))  {
            // Access to config, request, and response
        } else {
            // Just a stock error
        } */


    console.devlog('the error', err);
    throw(err)
    });

}

/**
 * Retrieves the current user's groups and pending invitations.
 * 
 * Makes a GET request to the usergroups endpoint to fetch both the groups 
 * the user belongs to and any pending group invitations.
 * 
 * @param includeAssistants - determines if the assistants hired by the doctors should be included in the response
 * 
 * @returns {Promise<AxiosResponse>} A promise that resolves to the axios response
 * containing the user's groups and invitations data
 * 
 * @throws {Error | AxiosError} Throws an error if the API request fails
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await myGroupsAndInvites();
 *   console.log(response.data);
 * } catch (error) {
 *   console.error('Failed to fetch groups and invites:', error);
 * }
 * ```
 */
export const myGroupsAndInvites = async (includeAssistants?: boolean) => {

    const queryString = new URLSearchParams();
    if(includeAssistants){
      queryString.append('includeAssistants', 'true')
    }
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${environment.apiUrl}api/usergroups/myGroupsAndInvites?${queryString.toString()}`,
      headers: {},
    };

    console.devlog(config, "configgg")

    return axios
      .request(config)
      .then((response) => {
        return response;
      })
      .catch((err: Error | AxiosError) => {

        console.devlog(err, "error from my groups and invites");
        throw err;
      });
}

export const inviteToProfessionalGroup = async (groupDocumentId: string, email: string) => {

  const payload = deepTrimStrings({
    invitee_email: email,
    user_group: groupDocumentId,
  });

  let data = JSON.stringify({
    "invitee_email": payload.invitee_email,
    "user_group": payload.user_group
  });

  let config = {
    method: 'post',
    maxBodyLength: Infinity,
    url: `${environment.apiUrl}api/user-group-invites/inviteToProfessionalGroup`,
    headers: { 
      'Content-Type': 'application/json', 
    },
    data : data
  };

  return axios.request(config)
  .then((response) => {
    console.devlog(JSON.stringify(response.data));
    return response;
  })
  .catch((error) => {
    console.devlog(error);
    throw error;
  });
}

export const sendProfessionalRegistrationInvite = async(email: string, groupDocumentId: string) => {

  const payload = deepTrimStrings({
    email,
    groupId: groupDocumentId,
  });

  let data = JSON.stringify({
    "email": payload.email,
    "groupId": payload.groupId
  });

  let config = {
    method: 'post',
    maxBodyLength: Infinity,
    url: `${environment.apiUrl}api/user-group-invites/sendProfessionalRegistrationInvite`,
    headers: {
      'Content-Type': 'application/json',
    },
    data: data
  }

  return axios.request(config)
  .then((response) => {
    console.devlog(JSON.stringify(response.data));
    return response;
  })
  .catch((error) => {
    console.devlog(error);
    throw error;
  });
 
}

export const acceptInviteToProfessionalGroup = async (groupDocumentId: string, reject?: boolean, code?: string) => {

    // construct the queryString only based on the params that are defined
    const queryString = new URLSearchParams();
    if (code) queryString.append('code', code);
    if (groupDocumentId) queryString.append('groupID', groupDocumentId);
    if (reject !== undefined) queryString.append('reject', reject.toString());

    let config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${environment.apiUrl}api/user-group-invites/acceptInviteToProfessionalGroup/?${queryString.toString()}`,
      headers: { 
      },
    };

    return axios.request(config)
    .then((response) => {
      console.devlog(JSON.stringify(response.data));
      return response;
    })
    .catch((error) => {
      console.devlog(error);
      throw error;
    });

}

export const leaveOrRemoveFromGroup = async (groupDocumentId: string, removeMemberDocumentId?: string, leave?: boolean ) => {

  if(leave && removeMemberDocumentId){
    throw new Error("Either leave or removeMemberDocumentId must be provided, not both.");
  }

  let data = JSON.stringify({
    "groupID": groupDocumentId,
    "leave": leave,
    "removeMemberID": removeMemberDocumentId
  });

  let config = {
    method: 'post',
    maxBodyLength: Infinity,
    url: `${environment.apiUrl}api/usergroups/leaveOrRemoveFromGroup`,
    headers: { 
      'Content-Type': 'application/json', 
    },
    data : data
  };

  return axios.request(config)
  .then((response) => {
    console.devlog(JSON.stringify(response.data));
    return response;
  })
  .catch((error) => {
    console.devlog(error);
    throw error;
  });


}


export const myDirectEmployees = async (userDocumentId: string)=>{
  let config = {
    method: "get",
    maxBodyLenght: Infinity,
    url: `${environment.apiUrl}api/user-relations/find-checked?filters[principal][documentId][$eq][0]=${userDocumentId}&filters[relationType][$eq][1]=employee`,
    headers: {}
  };
  return axios
  .request(config)
  .then((response) => {
    return response;
  })
  .catch((err: Error | AxiosError) => {
    console.devlog("Error fetching my direct Employees:", err)
    throw err;
  })
}

export const deleteRelation = async (relationDocumentId: string)=>{
  let config = {
    method: "delete",
    maxBodyLenght: Infinity,
    url: `${environment.apiUrl}api/user-relations/delete-checked/${relationDocumentId}`,
    headers: {}
  };
  return axios
  .request(config)
  .then((response)=>{
    return response;
  })
  .catch((err: Error | AxiosError) =>{
    console.devlog("Error deleting a relation:", err)
    throw err;
  })
}

// for assistants, to get the colleauges that work for the same doctor
export const getMyTeams = async () => {
  let config = {
    method: "GET",
    maxBodyLength: Infinity,
    url: `${environment.apiUrl}api/user-relations/getMyTeams`,
    headers: {}
  };

  return axios
    .request(config)
    .then((response)=> {
      return response;
    })
    .catch((err: Error | AxiosError) => {
      console.devlog("Error fetching teams:", err);
      throw err;
    })
}

export const inviteEmployee = async (email: string, birthDate: string): Promise<AxiosResponse<EmployeeInvite['inviteEmployee']['ResponseData']['Success']>> => {

  const data: EmployeeInvite['inviteEmployee']['PostData'] = {email, birthDate};
  let config = {
    method: 'post',
    maxBodyLength: Infinity,
    url: `${environment.apiUrl}api/employee-invites/inviteEmployee`,
    headers: { 'Content-Type': 'application/json' },
    data: JSON.stringify(data)
  }
  return axios.request(config)
    .then((response) => response)
    .catch((err: Error | AxiosError) => {
      console.devlog("Error inviting employee:", err);
      throw err;
    });
}

export const getMyEmployeeInvites = async (): Promise<AxiosResponse<EmployeeInvite['getMyInvites']['ResponseData']['Success']>> => {
  let config = {
    method: "get",
    maxBodyLength: Infinity,
    url: `${environment.apiUrl}api/employee-invites/getMyInvites`,
    headers: {}
  };

  return axios.request(config)
    .then((response) => response)
    .catch((err: Error | AxiosError) => {
      console.devlog("Error fetching employee invites:", err);
      throw err;
    });
}

export const acceptEmployeeInvite = async (code: string, reject?: boolean, birthDate?: string): Promise<AxiosResponse<EmployeeInvite['getMyInvites']['ResponseData']['Success']>> => {
  const data: EmployeeInvite['acceptInvite']['PostData'] = { code, reject, birthDate };
  let config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `${environment.apiUrl}api/employee-invites/acceptInvite`,
    headers: { 'Content-Type': 'application/json' },
    data: JSON.stringify(data)
  };

  return axios.request(config)
    .then((response) => response)
    .catch((err: Error | AxiosError) => {
      console.devlog("Error accepting employee invite:", err);
      throw err;
    });
}

export const getAllowedPrincipals = async() => {

  let config = {
    method: 'get',
    maxBodyLenght: Infinity,
    url: `${environment.apiUrl}api/user-relations/allowed-principals`,
    headers: {}
  };

  return axios
  .request(config)
  .then((response) => {
    return response;
  }).catch((err: Error | AxiosError) => {
    console.devlog("Error fetching all allowed principals:", err)
    throw err;
  })
}
